<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 move-modal-overlay flex items-center justify-center z-[70] p-4"
        role="presentation"
        @click.self="onCancel"
      >
        <div
          class="move-card move-modal-card"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="describedBy"
        >
          <div class="move-modal-card__header">
            <div>
              <h3 :id="titleId" class="move-modal-card__title">{{ title }}</h3>
              <p v-if="subtitle" :id="subtitleId" class="move-modal-card__subtitle">{{ subtitle }}</p>
            </div>
            <button
              type="button"
              class="move-icon-btn"
              aria-label="Close"
              :disabled="busy"
              @click="onCancel"
            >
              <span class="material-icons">close</span>
            </button>
          </div>

          <div
            v-if="hasDetail"
            class="rounded-xl px-4 py-3 mb-5"
            :style="detailStyle"
          >
            <slot name="detail">
              <p v-if="detailTitle" class="font-semibold move-text break-words">{{ detailTitle }}</p>
              <p
                v-if="detailBody"
                class="text-sm move-text-muted leading-relaxed"
                :class="{ 'mt-1': detailTitle }"
              >
                {{ detailBody }}
              </p>
            </slot>
          </div>

          <p
            v-if="body"
            :id="bodyId"
            class="text-sm move-text-muted leading-relaxed mb-5"
          >
            {{ body }}
          </p>

          <div v-if="error" class="move-alert-error mb-4">{{ error }}</div>

          <div class="flex gap-3">
            <button
              type="button"
              class="move-btn-ghost flex-1 justify-center"
              :disabled="busy"
              @click="onCancel"
            >
              {{ cancelLabel }}
            </button>
            <button
              type="button"
              class="flex-1 justify-center"
              :class="variant === 'danger' ? 'move-btn-danger' : 'move-btn-primary'"
              :disabled="busy"
              @click="$emit('confirm')"
            >
              <span v-if="busy" class="material-icons animate-spin text-base">sync</span>
              <span v-else-if="confirmIcon" class="material-icons text-base">{{ confirmIcon }}</span>
              <span>{{ busy ? busyLabel : confirmLabel }}</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, onBeforeUnmount, useSlots, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    default: ''
  },
  body: {
    type: String,
    default: ''
  },
  detailTitle: {
    type: String,
    default: ''
  },
  detailBody: {
    type: String,
    default: ''
  },
  detailTone: {
    type: String,
    default: 'neutral'
  },
  variant: {
    type: String,
    default: 'danger'
  },
  confirmLabel: {
    type: String,
    default: 'Confirm'
  },
  confirmIcon: {
    type: String,
    default: ''
  },
  cancelLabel: {
    type: String,
    default: 'Cancel'
  },
  busyLabel: {
    type: String,
    default: 'Working…'
  },
  busy: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const uid = Math.random().toString(36).slice(2, 9)
const titleId = `move-confirm-title-${uid}`
const subtitleId = `move-confirm-subtitle-${uid}`
const bodyId = `move-confirm-body-${uid}`

const slots = useSlots()
const hasDetail = computed(() => Boolean(
  props.detailTitle || props.detailBody || slots.detail
))

const describedBy = computed(() => {
  const ids = []
  if (props.subtitle) ids.push(subtitleId)
  if (props.body) ids.push(bodyId)
  return ids.length ? ids.join(' ') : undefined
})

const detailStyle = computed(() => {
  if (props.detailTone === 'danger') {
    return {
      background: 'var(--move-danger-soft)',
      border: '1px solid var(--move-border)'
    }
  }
  if (props.detailTone === 'accent') {
    return {
      background: 'var(--move-accent-soft)',
      border: '1px solid var(--move-border)'
    }
  }
  return {
    background: 'var(--move-surface-elevated)',
    border: '1px solid var(--move-border)'
  }
})

function onCancel () {
  if (props.busy) return
  emit('update:modelValue', false)
  emit('cancel')
}

function onKeydown (event) {
  if (event.key === 'Escape') {
    event.preventDefault()
    onCancel()
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      window.addEventListener('keydown', onKeydown)
    } else {
      window.removeEventListener('keydown', onKeydown)
    }
  }
)

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>
